/**
 * Every course with the words it came from, and — where the service allows
 * it — a way to fix what was misread.
 *
 * The verbatim column is the point of the whole table: it is what lets a
 * reviewer check one line against the instrument, and it is why the plugin's
 * version never truncates. A clipped quote is exactly the check this table
 * exists for, failing quietly, so the column wraps instead.
 *
 * Confidence rides along because the extractor is instructed to flag rather
 * than guess, and a low-confidence course is where a reviewer should look
 * first.
 *
 * Editing never touches `verbatim_text`. The quoted source is the document's
 * own words and the thing a correction is checked against; a table able to
 * rewrite it would make the certificate's diff meaningless. What an edit
 * changes is the measured value, and the service is what changes it — this
 * only collects the typing. Parsing lives in `../corrections`, which is
 * tested; this file draws it.
 */

import { useState } from "preact/hooks";
import { courseBearing, courseDistance, curveText, unitLabel } from "../format";
import {
  BadValue,
  changed,
  currentText,
  currentValue,
  editableFields,
  parseValue,
} from "../corrections";
import type { Call, CorrectableField } from "../types";

export interface PendingChange {
  field: CorrectableField;
  before: unknown;
  after: unknown;
}

export interface EditContext {
  kind: "boundary" | "tie";
  busy: boolean;
  onSave: (callIndex: number, changes: PendingChange[], note: string) => void;
}

export interface CallTableProps {
  calls: Call[];
  unit: string;
  /** Present only where editing is offered. Read-only tables omit it. */
  edit?: EditContext;
}

export function CallTable({ calls, unit, edit }: CallTableProps) {
  const mark = unitLabel(unit);
  const [editing, setEditing] = useState<number | null>(null);

  return (
    <div class="table-wrap">
      <table class="calls">
        <thead>
          <tr>
            <th>#</th>
            <th>type</th>
            <th>bearing</th>
            <th class="right">dist ({mark})</th>
            <th>curve</th>
            <th>verbatim source text</th>
            {edit && <th />}
          </tr>
        </thead>
        <tbody>
          {calls.map((call, index) => (
            <>
              <tr
                key={index}
                class={call.confidence === "low" ? "low" : undefined}
              >
                <td>{index + 1}</td>
                <td>{call.call_type}</td>
                <td>{courseBearing(call)}</td>
                <td class="right">{courseDistance(call)}</td>
                <td>{curveText(call, unit)}</td>
                <td class="verbatim">
                  {call.verbatim_text}
                  {call.confidence === "low" && (
                    <span class="flag"> low confidence</span>
                  )}
                  {call.monument && (
                    <span class="muted"> · {call.monument}</span>
                  )}
                </td>
                {edit && (
                  <td>
                    <button
                      type="button"
                      class="link"
                      onClick={() =>
                        setEditing(editing === index ? null : index)
                      }
                      title="Correct a value the service misread. The document is re-verified and re-signed, and the change is printed on the certificate."
                    >
                      {editing === index ? "cancel" : "correct"}
                    </button>
                  </td>
                )}
              </tr>
              {edit && editing === index && (
                <tr class="editor-row">
                  <td colSpan={7}>
                    <CallEditor
                      call={call}
                      busy={edit.busy}
                      onCancel={() => setEditing(null)}
                      onSave={(changes, note) => {
                        setEditing(null);
                        edit.onSave(index, changes, note);
                      }}
                    />
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One course's editable values, prefilled with what was extracted.
 *
 * Every field at once rather than one cell at a time, because a misread curve
 * is usually wrong in two places, and because the service takes a list — one
 * round trip, one re-signature, one row per field on the certificate.
 */
function CallEditor({
  call,
  busy,
  onCancel,
  onSave,
}: {
  call: Call;
  busy: boolean;
  onCancel: () => void;
  onSave: (changes: PendingChange[], note: string) => void;
}) {
  const fields = editableFields(call);
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields.map((f) => [f.field, currentText(call, f.field)]),
    ),
  );
  const [note, setNote] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  function save() {
    const changes: PendingChange[] = [];
    for (const spec of fields) {
      let after: unknown;
      try {
        after = parseValue(spec.kind, draft[spec.field] ?? "");
      } catch (failure) {
        setProblem(
          failure instanceof BadValue ? failure.message : String(failure),
        );
        return;
      }
      const before = currentValue(call, spec.field);
      if (changed(before, after))
        changes.push({ field: spec.field, before, after });
    }
    if (!changes.length) {
      setProblem("Nothing changed.");
      return;
    }
    onSave(changes, note);
  }

  return (
    <div class="call-editor">
      <p class="muted small">
        What does the instrument actually say? The document is re-verified
        against your reading and re-signed, and every change is printed on the
        certificate beside what was extracted.
      </p>
      <div class="editor-fields">
        {fields.map((spec) => (
          <label key={spec.field}>
            <span class="muted small">{spec.label}</span>
            <input
              value={draft[spec.field] ?? ""}
              placeholder={spec.kind === "bearing" ? "N 87°30'00\" E" : ""}
              disabled={busy}
              onInput={(event) =>
                setDraft({
                  ...draft,
                  [spec.field]: (event.target as HTMLInputElement).value,
                })
              }
            />
          </label>
        ))}
        <label class="wide-field">
          <span class="muted small">note</span>
          <input
            value={note}
            placeholder="why — e.g. source text reads 149.5"
            disabled={busy}
            onInput={(event) =>
              setNote((event.target as HTMLInputElement).value)
            }
          />
        </label>
      </div>
      {problem && <p class="reason">{problem}</p>}
      <p class="place-row">
        <button type="button" class="primary" disabled={busy} onClick={save}>
          {busy ? "Re-verifying…" : "Correct and re-verify"}
        </button>
        <button type="button" class="link" disabled={busy} onClick={onCancel}>
          cancel
        </button>
      </p>
    </div>
  );
}
